import { FabricObject, AttendeeData, TableData } from '../types';

/**
 * Merges Fabric.js layout data with attendee data from the API.
 * This function ensures that the Fabric.js chairs reflect the latest attendee assignments.
 */
export const mergeFabricDataWithAPIData = (
  fabricJsJsonData: any,
  // Keyed by Table Id, not Table Label: labels are display-only and not unique
  // (ADR-0002), so two Tables sharing a name must stay two Tables here.
  attendeesByTable: { [tableId: string]: AttendeeData[] }
) => {
  // Deep clone the Fabric.js data to avoid direct mutation of the original design template
  const mergedData = JSON.parse(JSON.stringify(fabricJsJsonData)); 
  
  console.log('Starting merge of Fabric.js data with API data...');
  console.log('API provided tables:', Object.keys(attendeesByTable));
  
  const tablesInLayout: string[] = []; // Track Table Ids that exist in the Fabric.js layout

  // FIRST: Global clearing of all attendee data from all chairs in the Fabric.js layout.
  // This is crucial to ensure old assignments are removed before new ones are applied.
  const clearAttendeeDataFromObjects = (obj: FabricObject) => {
    if (obj.isChair) {
      if (obj.attendeeData) {
        // console.log(`🧹 CLEARED: Removed attendee data from a chair.`);
        delete obj.attendeeData; // Remove attendee data from the chair
      }
    }
    if (obj.type === 'group' && obj.objects) {
      obj.objects.forEach(clearAttendeeDataFromObjects);
    }
  };

  if (mergedData.objects) {
    console.log('🧹 GLOBAL CLEAR: Clearing all existing attendee data from chairs in the layout...');
    mergedData.objects.forEach(clearAttendeeDataFromObjects);
    console.log('✅ GLOBAL CLEAR: Completed clearing all chairs.');
  }
  
  // Recursive function to update fabric objects with API data
  const updateFabricObjectWithAPIData = (obj: FabricObject): any => {
    if (obj.type === 'group' && obj.isTable && obj.tableData) {
      const tableId = obj.tableData.tableId;
      const tableName = obj.tableData.tableName || obj.tableData.tableNumber || tableId;
      if (!tableId) {
        // Every join inside the application is on the Table Id (ADR-0002); a
        // Table without one is the same Table the module skipped when it read
        // the room, so it draws empty rather than matching somebody by name.
        console.warn('Fabric.js table group found without a tableId:', obj);
        return obj;
      }
      tablesInLayout.push(tableId); // Mark this table as existing in the layout

      const apiAttendeesForTable = attendeesByTable[tableId] || [];

      console.log(`🔗 MERGING: Table "${tableName}" in layout with ${apiAttendeesForTable.length} attendees from API.`);
      
      // Calculate capacity based on chairs in the Fabric.js design
      const chairCount = obj.objects ? obj.objects.filter((child: FabricObject) => child.isChair).length : 0;
      obj.tableData.capacity = chairCount;
      
      // Limit attendees to the actual chair capacity
      const limitedAttendees = apiAttendeesForTable.slice(0, chairCount);
      obj.tableData.attendees = [...limitedAttendees];
      obj.tableData.emptySeats = Math.max(0, chairCount - limitedAttendees.length);
      
      // Log overflow warning if there are more attendees than chairs
      if (apiAttendeesForTable.length > chairCount) {
        const overflowCount = apiAttendeesForTable.length - chairCount;
        console.warn(`⚠️ OVERFLOW: Table "${tableName}" has ${chairCount} chairs but ${apiAttendeesForTable.length} attendees assigned. ${overflowCount} attendees will not be shown.`);
        console.warn('Overflow attendees:', apiAttendeesForTable.slice(chairCount).map(a => a.name));
      }
      
      // Assign attendees to chairs in the Fabric.js layout
      if (obj.objects) {
        let attendeeIndex = 0;
        obj.objects.forEach((childObj: FabricObject) => {
          if (childObj.isChair) {
            if (attendeeIndex < limitedAttendees.length) {
              childObj.attendeeData = limitedAttendees[attendeeIndex];
              console.log(`🪑 ASSIGNED: Chair in "${tableName}" → "${limitedAttendees[attendeeIndex].name}"`);
              attendeeIndex++;
            } else {
              // If no more attendees, the chair remains without attendeeData (already cleared)
              // console.log(`🪑 EMPTY: Chair in "${tableName}" → Empty`);
            }
          }
          // Recursively process nested objects within the table group
          updateFabricObjectWithAPIData(childObj);
        });
      }
    } else if (obj.objects) {
      // Recursively process other nested objects (e.g., nested groups that are not tables)
      obj.objects.forEach((childObj: FabricObject) => updateFabricObjectWithAPIData(childObj));
    }
    
    return obj;
  };
  
  // Iterate through the top-level objects in Fabric.js data to start the merge process
  if (mergedData.objects) {
    mergedData.objects.forEach((obj: FabricObject) => updateFabricObjectWithAPIData(obj));
  }
  
  // Identify tables from API data that do not exist in the Fabric.js layout
  const unmappedAPITables = Object.keys(attendeesByTable).filter(tableId =>
    !tablesInLayout.includes(tableId)
  );
  
  if (unmappedAPITables.length > 0) {
    console.warn('⚠️ WARNING: Attendees found for tables that do not exist in the current floor map layout:');
    unmappedAPITables.forEach(tableId => {
      const attendees = attendeesByTable[tableId];
      console.warn(`  Table "${tableId}" (${attendees.length} attendees):`, attendees.map(a => a.name));
    });
  }
  
  console.log('✅ MERGE COMPLETE: Fabric.js data updated with API attendee assignments.');
  return mergedData;
}; 